import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { serviceAreaEndpoints } from '@/lib/api/endpoints';
import { serviceAreaKeys, onboardingKeys } from '@/lib/queryKeys';

export interface BulkServiceAreaItem {
  zipCode: string;
  city: string;
  state: string;
}

export interface BulkUpdateServiceAreasInput {
  add: BulkServiceAreaItem[];
  remove: string[];
}

export interface BulkUpdateServiceAreasResult {
  added: number;
  removed: number;
}

interface BulkResponse {
  added: number;
  removed: number;
  total: number;
}

/**
 * Single-call bulk service-area save.
 *
 * The ops `POST /service-areas/bulk` handler is now the orchestration
 * point: after the ops DB transaction commits, the same handler mirrors
 * the selection into the scheduler-side `SchedulerServiceArea` table and
 * enqueues the Ziptax `TAX_POPULATION_QUEUE` job. The FE therefore makes
 * exactly one network request even for a 500+ ZIP polygon-resolved
 * selection, instead of the three sequential calls this hook used to fan
 * out (ops → scheduler → tax-zones/populate).
 *
 * The hook still throws on failure so the caller can surface a toast —
 * the BE handler is responsible for atomicity (any of ops save, scheduler
 * mirror, or populate enqueue failing bubbles up as a single error).
 */
export function useBulkUpdateServiceAreas() {
  const queryClient = useQueryClient();

  return useApiMutation<BulkUpdateServiceAreasResult, BulkUpdateServiceAreasInput>({
    // Caller handles its own success/error toasts; suppress the auto one
    // so we don't double-fire.
    showErrorToast: false,
    mutationFn: async ({ add, remove }) => {
      const opsResult = await apiPost<BulkResponse>(operationsClient, serviceAreaEndpoints.bulk(), {
        add,
        remove,
      });

      return { added: opsResult.added, removed: opsResult.removed };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: serviceAreaKeys.all() });
      // Service areas are the first onboarding task — refresh the checklist so
      // the gate/banner reflect completion immediately.
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all() });
    },
  });
}
